/**
 * Composant EcheancierDialog - Dialogue pour créer un échéancier de paiement
 */

import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { CalendarClock, Calendar, Plus, X, CheckCircle } from 'lucide-react';

// Dialogue d'édition manuel des échéances
export const EcheancierDialog = ({
  open,
  onOpenChange,
  selectedFacture,
  echeancierData,
  setEcheancierData,
  onConfirm,
  onCancel
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="echeancier-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-purple-600" />
            Créer un échéancier
          </DialogTitle>
          <DialogDescription>
            Facture: {selectedFacture?.facture_numero} - Total: {(selectedFacture?.montant_total || 0).toLocaleString('fr-FR')} €
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="text-sm text-slate-500 mb-2">
            Définissez les échéances de paiement. Le total doit correspondre au montant de la facture.
          </div>
          
          {echeancierData.map((ech, idx) => (
            <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <span className="text-sm font-medium w-8">#{idx + 1}</span>
              <Input
                type="date"
                value={ech.date_echeance}
                onChange={(e) => {
                  const newData = [...echeancierData];
                  newData[idx].date_echeance = e.target.value;
                  setEcheancierData(newData);
                }}
                className="flex-1"
              />
              <Input
                type="number"
                value={ech.montant}
                onChange={(e) => {
                  const newData = [...echeancierData];
                  newData[idx].montant = parseFloat(e.target.value) || 0;
                  setEcheancierData(newData);
                }}
                className="w-28"
              />
              <span className="text-sm">€</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEcheancierData(echeancierData.filter((_, i) => i !== idx))}
                className="text-red-500 hover:text-red-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          
          <Button
            variant="outline"
            onClick={() => setEcheancierData([...echeancierData, { 
              date_echeance: new Date().toISOString().split('T')[0], 
              montant: 0 
            }])}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Ajouter une échéance
          </Button>
          
          <div className="p-3 bg-purple-50 rounded-lg flex justify-between">
            <span className="font-medium">Total échéancier:</span>
            <span className={`font-bold ${
              echeancierData.reduce((sum, e) => sum + e.montant, 0) === (selectedFacture?.montant_total || 0)
                ? 'text-green-600' : 'text-orange-600'
            }`}>
              {echeancierData.reduce((sum, e) => sum + e.montant, 0).toLocaleString('fr-FR')} €
              {echeancierData.reduce((sum, e) => sum + e.montant, 0) !== (selectedFacture?.montant_total || 0) && (
                <span className="text-xs ml-2">(attendu: {(selectedFacture?.montant_total || 0).toLocaleString('fr-FR')} €)</span>
              )}
            </span>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Annuler</Button>
          <Button 
            onClick={onConfirm} 
            className="bg-purple-600 hover:bg-purple-700"
            disabled={echeancierData.length === 0}
            data-testid="confirm-echeancier-btn"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Créer l'échéancier
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Dialogue de création rapide d'échéancier
export const EcheancierCreationDialog = ({
  open,
  onOpenChange,
  selectedDevis,
  echeancierCreationData,
  setEcheancierCreationData,
  onConfirm,
  onCancel
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-indigo-600 flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Créer un échéancier de paiement
          </DialogTitle>
          <DialogDescription>
            {selectedDevis && (
              <span>
                Facture: <strong>{selectedDevis.facture_numero || selectedDevis.devis_id?.slice(0, 8)}</strong>
                <br />
                Montant à échelonner: <strong>{echeancierCreationData.montant_total?.toLocaleString('fr-FR')} €</strong>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="nb-echeances">Nombre d'échéances</Label>
            <Input
              id="nb-echeances"
              type="number"
              min="2"
              max="24"
              value={echeancierCreationData.nb_echeances}
              onChange={(e) => setEcheancierCreationData({...echeancierCreationData, nb_echeances: parseInt(e.target.value) || 2})}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="date-debut">Date de la première échéance</Label>
            <Input
              id="date-debut"
              type="date"
              value={echeancierCreationData.date_debut}
              onChange={(e) => setEcheancierCreationData({...echeancierCreationData, date_debut: e.target.value})}
            />
          </div>
          
          {/* Aperçu de l'échéancier */}
          <div className="bg-slate-50 rounded-lg p-3 mt-4">
            <p className="text-xs text-slate-500 font-medium mb-2">Aperçu de l'échéancier :</p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {Array.from({ length: echeancierCreationData.nb_echeances }, (_, i) => {
                const montantEcheance = Math.round((echeancierCreationData.montant_total / echeancierCreationData.nb_echeances) * 100) / 100;
                const dateEcheance = new Date(echeancierCreationData.date_debut);
                dateEcheance.setMonth(dateEcheance.getMonth() + i);
                return (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-slate-600">Échéance {i + 1} - {dateEcheance.toLocaleDateString('fr-FR')}</span>
                    <span className="font-medium text-slate-900">{montantEcheance.toLocaleString('fr-FR')} €</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Annuler
          </Button>
          <Button
            className="bg-indigo-500 hover:bg-indigo-600"
            onClick={onConfirm}
          >
            <Calendar className="h-4 w-4 mr-2" />
            Créer l'échéancier
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EcheancierDialog;
